import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { storage } from '@/lib/utils';

export type ThemePref = 'light' | 'dark' | 'system';

const ThemeContext = createContext<{ pref: ThemePref; setPref: (p: ThemePref) => void; dark: boolean }>({
  pref: 'system',
  setPref: () => undefined,
  dark: false,
});

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    try {
      const saved = localStorage.getItem('cs_theme');
      return saved === 'light' || saved === 'dark' ? saved : 'system';
    } catch {
      return 'system';
    }
  });
  const [systemDark, setSystemDark] = useState(() => media().matches);

  useEffect(() => {
    const mq = media();
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const dark = pref === 'dark' || (pref === 'system' && systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute(
        'content',
        getComputedStyle(document.documentElement).getPropertyValue('--background').trim(),
      );
  }, [dark]);

  const setPref = (p: ThemePref) => {
    setPrefState(p);
    try {
      localStorage.setItem('cs_theme', p);
    } catch {
      storage.remove('cs_theme');
    }
  };

  return <ThemeContext.Provider value={{ pref, setPref, dark }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
